import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, type Category, type RankedSchool, type Season } from './api';
import { SchoolSearch } from './components/SchoolSearch';
import { CategorySelector } from './components/CategorySelector';
import { SeasonSelector } from './components/SeasonSelector';
import { Leaderboard } from './components/Leaderboard';
import { PledgePanel, type PledgeTarget } from './components/PledgePanel';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';

/** Tiny hash router: '' | '#/terms' | '#/privacy'. */
function useHashRoute(): [string, (r: string) => void] {
  const [route, setRoute] = useState(() => window.location.hash.replace(/^#/, ''));
  useEffect(() => {
    const on = () => setRoute(window.location.hash.replace(/^#/, ''));
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  const nav = useCallback((r: string) => {
    window.location.hash = r;
  }, []);
  return [route, nav];
}

export default function App() {
  const [route, nav] = useHashRoute();
  const [categories, setCategories] = useState<Category[]>([]);
  const [selected, setSelected] = useState('overall');
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [season, setSeason] = useState('all');
  const [current, setCurrent] = useState<Category | null>(null);
  const [schools, setSchools] = useState<RankedSchool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [thanks, setThanks] = useState<string | null>(null);
  const [pledge, setPledge] = useState<PledgeTarget | null>(null);

  const confName = useMemo(() => {
    const byId = new Map(categories.map((c) => [c.id, c.name]));
    return (id: string) => byId.get(id) ?? id;
  }, [categories]);

  // Handle the Stripe Checkout success return (?contributed=<schoolId>).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('contributed');
    if (id) {
      setThanks(id);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    api
      .listCategories()
      .then(({ categories }) => setCategories(categories))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load categories'));
  }, []);

  useEffect(() => {
    api
      .listSeasons()
      .then(({ seasons }) => setSeasons(seasons))
      .catch(() => {
        /* seasons are optional chrome; a failure just hides the switcher */
      });
  }, []);

  const loadBoard = useCallback(async (categoryId: string, seasonId: string) => {
    setLoading(true);
    setError(null);
    try {
      const { category, schools } = await api.seasonLeaderboard(seasonId, categoryId);
      setCurrent(category);
      setSchools(schools);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadBoard(selected, season);
  }, [selected, season, loadBoard]);

  // Contribute -> get a Stripe Checkout URL -> redirect the browser to it.
  const handleContribute = useCallback(
    async (schoolId: string, amountCents: number, handle?: string) => {
      const { url } = await api.contribute(schoolId, amountCents, handle);
      // Open-redirect guard: only navigate to an https Stripe host. The URL
      // comes from our own API, but validating the destination means a tampered
      // or misconfigured response can never send the browser to an arbitrary
      // site. javascript:/data: are rejected by the protocol check.
      let dest: URL;
      try {
        dest = new URL(url);
      } catch {
        throw new Error('Invalid checkout URL');
      }
      const host = dest.hostname;
      const stripeHost = host === 'stripe.com' || host.endsWith('.stripe.com');
      if (dest.protocol !== 'https:' || !stripeHost) {
        throw new Error('Unexpected checkout URL');
      }
      window.location.assign(dest.href);
    },
    [],
  );

  const backToBoard = useCallback(() => nav('/'), [nav]);

  if (route === '/terms') {
    return (
      <main className="app">
        <TermsPage onBack={backToBoard} />
      </main>
    );
  }
  if (route === '/privacy') {
    return (
      <main className="app">
        <PrivacyPage onBack={backToBoard} />
      </main>
    );
  }

  return (
    <main className="app">
      <header className="app__header">
        <div className="app__brand">
          <img src="/logo-medal.svg" alt="" className="app__logo" width={44} height={44} />
          <h1>School Rank</h1>
        </div>
        <p className="app__tagline">Buy points. Rank your school.</p>
      </header>

      <SchoolSearch conferenceName={confName} onSelect={setPledge} />

      {pledge && (
        <div className="pledge-overlay">
          <PledgePanel
            target={pledge}
            conferenceName={confName}
            onSubmit={handleContribute}
            onCancel={() => setPledge(null)}
          />
        </div>
      )}

      {thanks && (
        <p className="thanks" role="status">
          🎉 Points added! The board updates as soon as your payment confirms.
        </p>
      )}

      <section className="app__body">
        <div className="board__head">
          <h2 className="board__heading">Leaderboard</h2>
          <SeasonSelector seasons={seasons} selected={season} onSelect={setSeason} />
          <CategorySelector categories={categories} selected={selected} onSelect={setSelected} />
        </div>

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        {loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <Leaderboard
            category={current}
            schools={schools}
            conferenceName={confName}
            onSelect={setPledge}
          />
        )}
      </section>

      <footer className="app__footer">
        A novelty fan leaderboard. Points rank your school and are not affiliated
        with or endorsed by any institution or conference.
        <span className="app__footer-links">
          <a href="#/terms">Terms</a>
          <a href="#/privacy">Privacy</a>
        </span>
        <span className="app__footer-copy">© 2026 Eason Books LLC. All rights reserved.</span>
      </footer>
    </main>
  );
}
